/**
 * Client ของ Tool/Die Master (`/v1/tools`, D-108 ข้อ 4 และ 5) — ตามสิบเส้นทางที่
 * router จริงของเซิร์ฟเวอร์ serve (`modules/master_data/router.py`, `WO-P2-M11-009`)
 * ไม่ใช่แค่ตาม `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้ (ฝาแฝดของ `lib/ncr/client.ts`):
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีเครื่องมือ"
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - **ไม่มีการคำนวณสองตัวนับใหม่ที่นี่เลย** — ทั้ง `parseTool` และทุกฟังก์ชันที่เรียก
 *   มันอ่าน `cumulative_life_count`/`life_since_last_maintenance` ตรง ๆ จาก payload
 *   เป็นสตริง ไม่แปลงเป็นตัวเลขแล้วบวก/ลบ/เทียบกันเองที่นี่ (invariant ของ WO นี้:
 *   "THE COUNTERS ARE COMPUTED BY THE SERVER")
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateToolBody,
  LinkToolProductBody,
  LinkToolWorkCenterBody,
  PatchToolBody,
  RecordToolMaintenanceBody,
  Tool,
  ToolListPage,
  ToolMaintenanceEvent,
  ToolMaintenanceEventListPage,
  ToolProduct,
  ToolProductListPage,
  ToolWorkCenter,
  ToolWorkCenterListPage,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const TOOLS_PATH = '/v1/tools';

export function toolPath(toolId: string): string {
  return `${TOOLS_PATH}/${encodeURIComponent(toolId)}`;
}

export function toolProductsPath(toolId: string): string {
  return `${toolPath(toolId)}/products`;
}

export function toolWorkCentersPath(toolId: string): string {
  return `${toolPath(toolId)}/work-centers`;
}

export function toolMaintenanceEventsPath(toolId: string): string {
  return `${toolPath(toolId)}/maintenance-events`;
}

/** พารามิเตอร์ของ `GET /v1/tools` ที่ router จริงรับ — `tool_type` เท่านั้น ไม่มี
 * `plant_code` เลย (เครื่องมือเป็นข้อมูลระดับองค์กร — ดูหมายเหตุของ `types.ts`) */
export interface ToolListQuery {
  readonly tool_type?: string;
}

export function buildToolListQuery(query: ToolListQuery = {}): string {
  const params = new URLSearchParams();
  const toolType = query.tool_type?.trim() ?? '';
  if (toolType !== '') {
    params.append('tool_type', toolType);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/** ความล้มเหลวทุกแบบที่ router นี้ตอบจริง แยกเป็นสถานะของตัวเอง */
export type ToolFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ToolListResult =
  | { readonly status: 'ready'; readonly page: ToolListPage }
  | ToolFailure;

export type ToolResult = { readonly status: 'ready'; readonly tool: Tool } | ToolFailure;

export type ToolProductListResult =
  | { readonly status: 'ready'; readonly page: ToolProductListPage }
  | ToolFailure;

export type ToolProductResult =
  | { readonly status: 'ready'; readonly toolProduct: ToolProduct }
  | ToolFailure;

export type ToolWorkCenterListResult =
  | { readonly status: 'ready'; readonly page: ToolWorkCenterListPage }
  | ToolFailure;

export type ToolWorkCenterResult =
  | { readonly status: 'ready'; readonly toolWorkCenter: ToolWorkCenter }
  | ToolFailure;

export type ToolMaintenanceEventListResult =
  | { readonly status: 'ready'; readonly page: ToolMaintenanceEventListPage }
  | ToolFailure;

export type ToolMaintenanceEventResult =
  | { readonly status: 'ready'; readonly event: ToolMaintenanceEvent }
  | ToolFailure;

export interface ToolRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** `undefined` แปลว่า "ผิดรูป" — `null` เป็นค่าที่ถูกต้อง */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function nullableInteger(value: unknown): number | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

/**
 * เครื่องมือหนึ่งตัว — **สองตัวนับต้องเป็นสตริงเท่านั้น** (`AmountOut` ฝั่งเซิร์ฟเวอร์)
 * ค่าที่มาเป็น `number` ถือว่าผิดรูป ไม่ใช่ปัดให้เข้ารูปเอง เพราะนั่นจะเป็นการเดาความ
 * แม่นยำที่หายไปแล้วตั้งแต่ต้นทาง (`invalid-response` ปลอดภัยกว่าตัวเลขที่แต่งขึ้น)
 */
export function parseTool(raw: unknown): Tool | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const toolId = requiredString(record.tool_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  const cumulativeLifeCount = requiredString(record.cumulative_life_count);
  const lifeSinceLastMaintenance = requiredString(record.life_since_last_maintenance);

  if (
    toolId === null ||
    createdAt === null ||
    updatedAt === null ||
    cumulativeLifeCount === null ||
    lifeSinceLastMaintenance === null
  ) {
    return null;
  }

  const toolCode = nullableString(record.tool_code);
  const toolType = nullableString(record.tool_type);
  const spec = nullableString(record.spec);
  const ratedLifeShots = nullableInteger(record.rated_life_shots);
  const lastMaintenanceAt = nullableString(record.last_maintenance_at);

  if (
    toolCode === undefined ||
    toolType === undefined ||
    spec === undefined ||
    ratedLifeShots === undefined ||
    lastMaintenanceAt === undefined
  ) {
    return null;
  }

  return {
    tool_id: toolId,
    tool_code: toolCode,
    tool_type: toolType,
    spec,
    rated_life_shots: ratedLifeShots,
    cumulative_life_count: cumulativeLifeCount,
    life_since_last_maintenance: lifeSinceLastMaintenance,
    last_maintenance_at: lastMaintenanceAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseToolListPage(raw: unknown): ToolListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }
  const items: Tool[] = [];
  for (const entry of record.items) {
    const parsed = parseTool(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return { items };
}

export function parseToolProduct(raw: unknown): ToolProduct | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const toolId = requiredString(record.tool_id);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const createdAt = requiredString(record.created_at);
  if (toolId === null || productId === null || productCode === null || createdAt === null) {
    return null;
  }
  return { tool_id: toolId, product_id: productId, product_code: productCode, created_at: createdAt };
}

export function parseToolProductListPage(raw: unknown): ToolProductListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const toolId = requiredString(record.tool_id);
  if (toolId === null || !Array.isArray(record.items)) {
    return null;
  }
  const items: ToolProduct[] = [];
  for (const entry of record.items) {
    const parsed = parseToolProduct(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return { tool_id: toolId, items };
}

export function parseToolWorkCenter(raw: unknown): ToolWorkCenter | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const toolId = requiredString(record.tool_id);
  const workCenterId = requiredString(record.work_center_id);
  const workCenterCode = requiredString(record.work_center_code);
  const plantCode = requiredString(record.plant_code);
  const createdAt = requiredString(record.created_at);
  if (
    toolId === null ||
    workCenterId === null ||
    workCenterCode === null ||
    plantCode === null ||
    createdAt === null
  ) {
    return null;
  }
  return {
    tool_id: toolId,
    work_center_id: workCenterId,
    work_center_code: workCenterCode,
    plant_code: plantCode,
    created_at: createdAt,
  };
}

export function parseToolWorkCenterListPage(raw: unknown): ToolWorkCenterListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const toolId = requiredString(record.tool_id);
  if (toolId === null || !Array.isArray(record.items)) {
    return null;
  }
  const items: ToolWorkCenter[] = [];
  for (const entry of record.items) {
    const parsed = parseToolWorkCenter(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return { tool_id: toolId, items };
}

export function parseToolMaintenanceEvent(raw: unknown): ToolMaintenanceEvent | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const eventId = requiredString(record.event_id);
  const toolId = requiredString(record.tool_id);
  const performedAt = requiredString(record.performed_at);
  const performedBy = requiredString(record.performed_by_employee_id);
  const createdAt = requiredString(record.created_at);
  if (
    eventId === null ||
    toolId === null ||
    performedAt === null ||
    performedBy === null ||
    createdAt === null
  ) {
    return null;
  }
  const note = nullableString(record.note);
  if (note === undefined) {
    return null;
  }
  return {
    event_id: eventId,
    tool_id: toolId,
    performed_at: performedAt,
    performed_by_employee_id: performedBy,
    note,
    created_at: createdAt,
  };
}

export function parseToolMaintenanceEventListPage(
  raw: unknown,
): ToolMaintenanceEventListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const toolId = requiredString(record.tool_id);
  if (toolId === null || !Array.isArray(record.items)) {
    return null;
  }
  const items: ToolMaintenanceEvent[] = [];
  for (const entry of record.items) {
    const parsed = parseToolMaintenanceEvent(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return { tool_id: toolId, items };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};
  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* -------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult = { readonly status: 'payload'; readonly payload: unknown } | ToolFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: ToolRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

function toToolResult(result: PayloadResult): ToolResult {
  if (result.status !== 'payload') {
    return result;
  }
  const tool = parseTool(result.payload);
  return tool === null ? { status: 'invalid-response' } : { status: 'ready', tool };
}

/** เหลือเฉพาะคีย์ที่ contract รับ — เขียนทีละตัวแทนการ spread เพื่อไม่ให้ฟิลด์ที่
 * เซิร์ฟเวอร์เป็นเจ้าของหลุดไปกับ state ฟอร์ม */
export function serializeCreateToolBody(body: CreateToolBody): Record<string, unknown> {
  return {
    ...(body.tool_code === undefined ? {} : { tool_code: body.tool_code }),
    ...(body.tool_type === undefined ? {} : { tool_type: body.tool_type }),
    ...(body.spec === undefined ? {} : { spec: body.spec }),
    ...(body.rated_life_shots === undefined ? {} : { rated_life_shots: body.rated_life_shots }),
  };
}

export function serializePatchToolBody(body: PatchToolBody): Record<string, unknown> {
  return {
    tool_code: body.tool_code,
    tool_type: body.tool_type,
    spec: body.spec,
    rated_life_shots: body.rated_life_shots,
  };
}

/** `GET /v1/tools` — ไม่มีขอบเขตโรงงาน (ดูหมายเหตุของ `types.ts`) */
export async function fetchTools(
  query: ToolListQuery = {},
  options: ToolRequestOptions = {},
): Promise<ToolListResult> {
  const result = await requestPayload(
    `${TOOLS_PATH}${buildToolListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseToolListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `POST /v1/tools` — ลงทะเบียนเครื่องมือใหม่ ตอบ `201` — สองตัวนับเริ่มที่ 0 */
export async function createTool(
  body: CreateToolBody,
  options: ToolRequestOptions = {},
): Promise<ToolResult> {
  return toToolResult(
    await requestPayload(
      TOOLS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateToolBody(body)),
      },
      options,
    ),
  );
}

/** `PATCH /v1/tools/{tool_id}` — แก้สิ่งที่เครื่องมือ *เป็น* ไม่ใช่สิ่งที่มัน *ทำมา* */
export async function patchTool(
  toolId: string,
  body: PatchToolBody,
  options: ToolRequestOptions = {},
): Promise<ToolResult> {
  return toToolResult(
    await requestPayload(
      toolPath(toolId),
      {
        method: 'PATCH',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializePatchToolBody(body)),
      },
      options,
    ),
  );
}

/** `GET /v1/tools/{tool_id}/products` — ส่วนประกอบที่เครื่องมือนี้ผลิตได้ */
export async function fetchToolProducts(
  toolId: string,
  options: ToolRequestOptions = {},
): Promise<ToolProductListResult> {
  const result = await requestPayload(
    toolProductsPath(toolId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseToolProductListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `POST /v1/tools/{tool_id}/products` — อนุมัติเครื่องมือนี้ให้ผลิตอีกหนึ่ง part number */
export async function linkToolProduct(
  toolId: string,
  body: LinkToolProductBody,
  options: ToolRequestOptions = {},
): Promise<ToolProductResult> {
  const result = await requestPayload(
    toolProductsPath(toolId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: body.product_id }),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const toolProduct = parseToolProduct(result.payload);
  return toolProduct === null
    ? { status: 'invalid-response' }
    : { status: 'ready', toolProduct };
}

/** `GET /v1/tools/{tool_id}/work-centers` — เครื่องจักรที่เครื่องมือนี้ใช้ได้ */
export async function fetchToolWorkCenters(
  toolId: string,
  options: ToolRequestOptions = {},
): Promise<ToolWorkCenterListResult> {
  const result = await requestPayload(
    toolWorkCentersPath(toolId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseToolWorkCenterListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `POST /v1/tools/{tool_id}/work-centers` — บันทึกว่าเครื่องมือนี้ใช้กับเครื่องจักรนี้ได้ */
export async function linkToolWorkCenter(
  toolId: string,
  body: LinkToolWorkCenterBody,
  options: ToolRequestOptions = {},
): Promise<ToolWorkCenterResult> {
  const result = await requestPayload(
    toolWorkCentersPath(toolId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ work_center_id: body.work_center_id }),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const toolWorkCenter = parseToolWorkCenter(result.payload);
  return toolWorkCenter === null
    ? { status: 'invalid-response' }
    : { status: 'ready', toolWorkCenter };
}

/** `GET /v1/tools/{tool_id}/maintenance-events` — ประวัติ PM ทั้งหมด ใหม่สุดก่อน */
export async function fetchToolMaintenanceEvents(
  toolId: string,
  options: ToolRequestOptions = {},
): Promise<ToolMaintenanceEventListResult> {
  const result = await requestPayload(
    toolMaintenanceEventsPath(toolId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseToolMaintenanceEventListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/tools/{tool_id}/maintenance-events` — **THE HEADLINE.** บันทึกครั้งนี้
 * คือสิ่งที่ตัดเส้นให้ `life_since_last_maintenance` เริ่มนับใหม่ที่เซิร์ฟเวอร์ ไม่มี
 * ตัวนับใดถูกเขียนที่นี่เลย
 */
export async function recordToolMaintenance(
  toolId: string,
  body: RecordToolMaintenanceBody,
  options: ToolRequestOptions = {},
): Promise<ToolMaintenanceEventResult> {
  const result = await requestPayload(
    toolMaintenanceEventsPath(toolId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        performed_at: body.performed_at,
        ...(body.note === undefined ? {} : { note: body.note }),
      }),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const event = parseToolMaintenanceEvent(result.payload);
  return event === null ? { status: 'invalid-response' } : { status: 'ready', event };
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromFailure(failure: ToolFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
